import { useCallback, useLayoutEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'
import { UseDropdownToggleProps } from '@restart/ui/DropdownToggle'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { FunctionTableColumnValue, TableAdapter } from '@makyoapp/makyo-common'
import { ArrowLeft16, ArrowRight16, ChevronDown, Table16 } from '@makyoapp/makyo-icon/icon'
import { DD, DDItem, useTheme } from '@makyoapp/makyo-ui'

import { COLUMN_TYPES_BG_COLOR } from '../../shared/ag-grid-constants'
import { ColumnTypeIcon } from '../ColumnTypeIcon'
import { useFilterColumnTypes } from './ColumnOrCustomDd'

interface TableColumnDdValue {
  table: string
  column: string
}

interface TableColumnDdToggleProps {
  id?: string
  active: boolean
  toggleProps: UseDropdownToggleProps
  value: TableColumnDdValue
  options: TableAdapter[]
  selectLabel?: React.ReactNode
}

function TableColumnDdToggle(props: TableColumnDdToggleProps) {
  const tb = props.options.find((tb) => tb.id === props.value.table)
  const cl = tb?.columns.find((c) => c.id === props.value.column)
  const { state: themeState } = useTheme()
  const isRtl = themeState.dir === 'rtl'

  return (
    <div className='sdd-toggle sdd-toggle-normal txt w-full'>
      {props.value.table && props.value.column ? (
        <div className='flex-c w-full txt'>
          <div className='flex-c txt'>
            <Table16 className='shrink-0' />
            <div className='txt'>{tb?.name}</div>
          </div>
          <div className='px-2'>{!isRtl ? <ArrowRight16 /> : <ArrowLeft16 />}</div>
          <div className='flex-c txt'>
            <ColumnTypeIcon type={cl?.type || ColumnTypes.Text} className='shrink-0' />
            <div className='txt'>{cl?.name}</div>
          </div>
        </div>
      ) : (
        <div className='flex-c w-full'>
          <Table16 className='shrink-0' />
          <div>{props.selectLabel || <Trans>Select Source</Trans>}</div>
        </div>
      )}
      <ChevronDown className='shrink-0' />
    </div>
  )
}

interface ColumnOrCustomWithTableDdProps {
  /**
   * html id
   */
  id: string
  /**
   * allowed column type
   */
  types?: ColumnTypes[]
  /**
   * excluded column type
   * applied after allowed types
   */
  excludeTypes?: ColumnTypes[]
  value: FunctionTableColumnValue
  onChange: (value: FunctionTableColumnValue) => void
  excludeColumns?: string[]
  tables: TableAdapter[]
}

export function ColumnOrCustomWithTableDd({
  id,
  types = [],
  excludeTypes = [],
  value,
  onChange,
  excludeColumns = [],
  tables,
}: ColumnOrCustomWithTableDdProps) {
  const [show, setShowRaw] = useBoolean(false)

  // should fill with table name
  const [showCol, setShowCol] = useState<TableAdapter | undefined>(undefined)

  // is animation currently disabled
  // the initial show of the menu should not have animation
  // this is use to disable that initial animation from running
  const [disableAnim, setDisableAnim] = useBoolean(true)
  const [openCol, setOpenCol] = useBoolean(false)

  const setShow = useCallback(
    (next: boolean) => {
      if (!next) {
        setShowCol(undefined)
        setOpenCol(false)
      }
      setShowRaw(next)
    },
    [setShowRaw, setShowCol, setOpenCol]
  )

  const columns = useFilterColumnTypes(undefined, showCol, types, excludeTypes, excludeColumns)

  useLayoutEffect(() => {
    if (!openCol && showCol !== undefined) {
      setTimeout(() => {
        setShowCol(undefined)
      }, 100)
    }
  }, [openCol, showCol, setShowCol])

  return (
    <DD
      id={id}
      onToggle={(e, meta) => {
        if (meta.source !== 'select') {
          setDisableAnim(true)
          setShow(!show)
        } else {
          setDisableAnim(false)
        }
      }}
      onSelect={(eventKey, e) => {
        e.preventDefault()
      }}
      show={show}
      className='w-full txt'
      toggleClassName='w-full txt'
      toggle={(active, toggleProps) => {
        return (
          <TableColumnDdToggle
            options={tables}
            active={active}
            toggleProps={toggleProps}
            value={{
              table: value?.table || '',
              column: `${value?.value || ''}`,
            }}
          />
        )
      }}
      menuClassName='sdd-menu-subc z-501 overflow-x-hidden mk-ag-sdd mk-tbl-dd'
      menuNoPad
      //menuDefaultSize={false}
      menuWrapperClassName='w-[200px]'
      //placement='bottom-start'
      portalMenu
    >
      <div
        className={clsx('mk-tbl-dd-tbl', openCol && 'open', !openCol && !disableAnim && 'close', openCol && 'hidden')}
      >
        {tables.map((opt) => {
          return (
            <DDItem
              className='sdd-item mk-tbl-item'
              key={opt.id}
              onClick={() => {
                setOpenCol(true)
                setShowCol(opt)
              }}
            >
              <Table16 className='mk-tbl-item-icon' />
              <div className='txt'>{opt.name}</div>
            </DDItem>
          )
        })}
      </div>
      <div className={clsx('mk-tbl-dd-col', openCol && 'col-open', !openCol && !disableAnim && 'col-close')}>
        <DDItem className='sdd-item mk-tbl-item mk-tbl-item-back' onClick={() => setOpenCol(false)}>
          <ArrowLeft16 />
          <div>
            <Trans>Back</Trans>
          </div>
        </DDItem>
        {columns?.map((opt) => {
          return (
            <DDItem
              className={clsx('sdd-item mk-tbl-item')}
              key={opt.id}
              onClick={() => {
                if (showCol) {
                  onChange({
                    isCustom: value?.isCustom ?? false,
                    table: showCol.id,
                    value: opt.id,
                  })
                  setShow(false)
                  setDisableAnim(true)
                  setOpenCol(false)
                  setShowCol(undefined)
                }
              }}
            >
              <div className={clsx('mk-tbl-col-icon', `${COLUMN_TYPES_BG_COLOR[opt.type] || 'bg-black'}`)}>
                <ColumnTypeIcon type={opt.type} size={16} />
              </div>
              <div className='txt'>{opt.name}</div>
            </DDItem>
          )
        })}
      </div>
    </DD>
  )
}
